import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  EyeOff,
  FileText,
  KeyRound,
  LockKeyhole,
  Presentation,
  ShieldCheck,
} from "lucide-react";

const DESTINATIONS = {
  workspace: "/g-bam-mission-assurance-review",
  "white-paper": "/api/gbam-white-paper",
  "solution-brief": "/api/gbam-solution-brief",
};

function normalizeDestination(value) {
  return Object.prototype.hasOwnProperty.call(DESTINATIONS, value)
    ? value
    : "workspace";
}

function AccessItem({ icon: Icon, title, detail }) {
  return (
    <div className="rounded-2xl border border-cyan-300/15 bg-white/[0.045] p-5">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10">
        <Icon className="h-5 w-5 text-cyan-200" aria-hidden="true" />
      </div>
      <h2 className="text-base font-semibold text-white">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-slate-300">{detail}</p>
    </div>
  );
}

export default function GBAMReviewerGateway() {
  const location = useLocation();
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState("");
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState("");

  const destination = useMemo(() => {
    const value = new URLSearchParams(location.search).get("next");
    return normalizeDestination(value);
  }, [location.search]);

  const openDestination = (next) => {
    const target = DESTINATIONS[normalizeDestination(next)];
    if (target.startsWith("/api/")) {
      window.location.assign(target);
      return;
    }
    navigate(target, { replace: true });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!passcode.trim()) {
      setError("Enter the reviewer passcode supplied by SALAR Technologies LLC.");
      return;
    }

    setError("");
    setStatus("submitting");

    try {
      const response = await fetch("/api/gbam-unlock", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ passcode, next: destination }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(payload.error || "The reviewer passcode is not valid.");
        setStatus("ready");
        return;
      }

      openDestination(payload.next || destination);
    } catch {
      setError("The reviewer workspace could not be unlocked. Please try again.");
      setStatus("ready");
    }
  };

  return (
    <>
      <Helmet>
        <title>DIU PROJ00672 Direct Review | SALAR Technologies LLC</title>
        <meta
          name="description"
          content="Passcode-protected direct-review workspace for DIU PROJ00672."
        />
        <link
          rel="canonical"
          href="https://www.salartechnologies.com/g-bam-mission-assurance-preview"
        />
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noarchive" />
      </Helmet>

      <main className="relative min-h-[calc(100vh-72px)] overflow-hidden bg-[#071525] text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(circle at 12% 18%, rgba(34,211,238,.14), transparent 31%), radial-gradient(circle at 88% 24%, rgba(37,99,235,.17), transparent 30%), linear-gradient(180deg, rgba(10,31,54,.35), rgba(3,13,24,.92))",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148,163,184,.45) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.45) 1px, transparent 1px)",
            backgroundSize: "42px 42px",
          }}
        />

        <section className="relative mx-auto max-w-6xl px-5 py-14 sm:px-8 lg:py-20">
          <div className="mb-8 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">
              <LockKeyhole className="h-4 w-4" aria-hidden="true" />
              Passcode-protected direct review
            </span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-medium tracking-wide text-slate-300">
              DIU PROJ00672
            </span>
          </div>

          <div className="grid items-start gap-10 lg:grid-cols-[1.08fr_.92fr]">
            <div className="pt-2">
              <p className="mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
                SALAR Technologies LLC
              </p>
              <h1 className="max-w-3xl text-4xl font-semibold leading-[1.08] tracking-[-0.035em] text-white sm:text-5xl lg:text-6xl">
                G-BAM mission-assurance engineering review
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-slate-300 sm:text-lg sm:leading-8">
                Enter the passcode supplied by SALAR to open the interactive
                engineering workspace, the corrected 29-page white paper, and
                the corrected 15-slide solution brief.
              </p>

              <div className="mt-9 grid gap-4 sm:grid-cols-3">
                <AccessItem
                  icon={ShieldCheck}
                  title="Interactive workspace"
                  detail="Architecture, evidence model, production logic, and reviewer calculations."
                />
                <AccessItem
                  icon={BookOpen}
                  title="29-page white paper"
                  detail="Full technical treatment with original schematics, equations, graphs, and calculations."
                />
                <AccessItem
                  icon={Presentation}
                  title="15-slide solution brief"
                  detail="Submission-facing executive and technical presentation."
                />
              </div>

              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.035] p-5 text-sm leading-6 text-slate-300">
                <EyeOff className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" aria-hidden="true" />
                <p>
                  This workspace is not linked from SALAR public navigation or
                  the sitemap. Search indexing, archival indexing, and normal
                  caching are disabled. The entry URL requires the reviewer
                  passcode every time it is opened.
                </p>
              </div>
            </div>

            <div className="rounded-[28px] border border-cyan-200/20 bg-[#0b1f35]/90 p-1 shadow-2xl shadow-cyan-950/40 backdrop-blur">
              <div className="rounded-[24px] border border-white/[0.06] bg-[#0a192b] p-6 sm:p-8">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-300/25 bg-cyan-300/10">
                  <KeyRound className="h-6 w-6 text-cyan-200" aria-hidden="true" />
                </div>
                <h2 className="mt-7 text-2xl font-semibold tracking-tight text-white">
                  Reviewer access
                </h2>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  The entry page always requires the passcode. Successful entry
                  creates a four-hour protected session for the workspace and
                  document links. No Google, Gmail, or Drive account is required.
                </p>

                <form className="mt-7" onSubmit={submit}>
                  <label
                    htmlFor="gbam-passcode"
                    className="mb-2 block text-sm font-semibold text-slate-200"
                  >
                    Passcode
                  </label>
                  <input
                    id="gbam-passcode"
                    name="passcode"
                    type="password"
                    autoComplete="current-password"
                    value={passcode}
                    onChange={(event) => setPasscode(event.target.value)}
                    disabled={status === "submitting"}
                    className="w-full rounded-xl border border-slate-500/40 bg-[#06111f] px-4 py-3.5 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300/70 focus:ring-4 focus:ring-cyan-400/10 disabled:cursor-wait disabled:opacity-70"
                    placeholder="Enter reviewer passcode"
                    aria-describedby={error ? "gbam-access-error" : undefined}
                    aria-invalid={Boolean(error)}
                  />

                  {error ? (
                    <p
                      id="gbam-access-error"
                      className="mt-3 rounded-xl border border-rose-400/25 bg-rose-400/10 px-4 py-3 text-sm text-rose-100"
                    >
                      {error}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-300 px-5 py-3.5 text-sm font-bold text-[#062036] transition hover:bg-cyan-200 focus:outline-none focus:ring-4 focus:ring-cyan-300/25 disabled:cursor-wait disabled:opacity-60"
                  >
                    {status === "submitting"
                      ? "Unlocking…"
                      : "Open direct-review workspace"}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </form>

                <div className="mt-7 border-t border-white/10 pt-6">
                  <div className="flex items-start gap-3 text-xs leading-5 text-slate-400">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" aria-hidden="true" />
                    <p>
                      Documents open through SALAR-domain links and remain
                      inaccessible until the passcode is accepted.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 border-t border-white/10 pt-6 text-xs leading-5 text-slate-400">
            <p>UNCLASSIFIED // COMPANY PROPRIETARY // DIRECT REVIEW ACCESS</p>
            <p className="mt-1">
              Vikrant Salar, Founder &amp; Principal ·
              vikrant@salartechnologies.com · salartechnologies.com
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
